import { __decorate } from "tslib";
import { css, LitElement, nothing } from 'lit';
import { html, literal } from 'lit/static-html.js';
import { property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { hasSlot } from '../../internal/slot.js';
import componentStyles from '../../styles/component.styles.js';
import '../icon/arc-icon.js';
import '../spinner/arc-spinner.js';
export default class ArcIconButton extends LitElement {
    constructor() {
        super(...arguments);
        this.hasLabel = false;
        this.label = '';
        this.active = false;
        this.disabled = false;
        this.loading = false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.handleSlotChange();
    }
    click() {
        this.button.click();
    }
    focus(options) {
        this.button.focus(options);
    }
    blur() {
        this.button.blur();
    }
    handleClick(event) {
        if (this.disabled || this.loading) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    handleSlotChange() {
        this.hasLabel = hasSlot(this);
    }
    render() {
        const isLink = !!this.href;
        const tag = isLink ? literal `a` : literal `button`;
        const btnStyles = {
            padding: this.hasLabel ? '0 0 var(--arc-spacing-small) 0' : null,
        };
        return html `
      <${tag}
        id="button"
        style=${styleMap(btnStyles)}
        ?disabled=${ifDefined(isLink ? undefined : this.disabled)}
        type="button"
        href=${ifDefined(this.href)}
        target=${ifDefined(this.target)}
        download=${ifDefined(this.download)}
        rel=${ifDefined(this.target ? 'noreferrer noopener' : undefined)}
        role="button"
        aria-disabled=${this.disabled ? 'true' : 'false'}
        aria-label=${this.label}
        tabindex=${this.disabled ? '-1' : '0'}
        @click=${this.handleClick}
      >
        <span id="iconWrapper" aria-hidden="true">
          <arc-icon id="icon" part="icon" name=${ifDefined(this.name)}></arc-icon>
          ${this.loading ? html `<arc-spinner id="loader"></arc-spinner>` : nothing}
        </span>
        ${this.hasLabel ? html `<span id="action"><slot @slotchange=${this.handleSlotChange}></slot></span>` : nothing}
      </${tag}>
    `;
    }
}
ArcIconButton.tag = 'arc-icon-button';
ArcIconButton.styles = [
    componentStyles,
    css `
      :host {
        display: inline-flex;
        cursor: pointer;
        --icon-color: rgb(var(--arc-font-color));
      }

      #button {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 100%;
        border: none;
        font-family: var(--arc-font-button);
        text-decoration: none;
        user-select: none;
        white-space: nowrap;
        vertical-align: middle;
        padding: 0;
        cursor: inherit;
        color: var(--icon-color);
        background: none;
        outline: none;
        -webkit-appearance: none;
      }

      #iconWrapper {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      #icon {
        padding: var(--arc-spacing-small);
        border-radius: 50%;
      }

      #action {
        font-size: var(--arc-font-size-xx-small);
        margin-top: -0.2rem;
      }

      /* Hover & Focus */
      :host(:not([disabled])) #button:hover #icon,
      :host(:not([disabled])) #button:focus-visible #icon {
        background: rgba(var(--arc-font-color), 10%);
      }

      /* Mouse down */
      :host(:not([disabled])) #button:active #icon {
        background: rgba(var(--arc-font-color), 30%);
      }

      /* Active */
      :host(:not([disabled])[active]) #button {
        border-bottom: calc(var(--arc-border-width) * 2) var(--arc-border-style) currentColor;
      }

      /* Disabled */
      :host([disabled]) #button {
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* Loading */
      :host([loading]) #icon {
        visibility: hidden;
      }

      #loader {
        position: absolute;
      }

      /* Prevent click events from firing on slots */
      #iconWrapper,
      #icon,
      #action {
        pointer-events: none;
      }
    `,
];
__decorate([
    query('#button')
], ArcIconButton.prototype, "button", void 0);
__decorate([
    state()
], ArcIconButton.prototype, "hasLabel", void 0);
__decorate([
    property({ type: String })
], ArcIconButton.prototype, "name", void 0);
__decorate([
    property({ type: String })
], ArcIconButton.prototype, "href", void 0);
__decorate([
    property({ type: String })
], ArcIconButton.prototype, "target", void 0);
__decorate([
    property({ type: String })
], ArcIconButton.prototype, "download", void 0);
__decorate([
    property({ type: String })
], ArcIconButton.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcIconButton.prototype, "active", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcIconButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ArcIconButton.prototype, "loading", void 0);
